import { defineStore } from "pinia";
import { ref } from "vue";
import { getDoc, getFirestore, onSnapshot, runTransaction, Timestamp, type Unsubscribe } from "firebase/firestore";

// เลข running ของใบเสร็จ (XXXX ใน YYYYMM-XXXX) เริ่มใหม่ทุกเดือน — ลอกแนวทางจาก Niceloop
//
// - localStorage เป็นตัวออกเลข อ่านใหม่ทุกครั้งที่ claim ทุกแท็บของเครื่องเดียวกันจึงใช้ลำดับเดียวกัน
//   ออนไลน์หรือออฟไลน์ก็ออกเลขได้ทันที key แยกตาม storeId + เดือน
// - Firestore เป็นแค่ "สมุดกลาง" doc เดียวต่อเดือน stores/{id}/receiptCounters/{YYYYMM} = { value }
//   ให้เครื่องอื่น sync ตาม (onSnapshot doc เดียว แทนการโหลดใบเสร็จทั้งเดือนจาก RTDB แบบเดิม)
// - ทุกบิลเข้าคิว bump ไว้ แล้ว flush ด้วย transaction ที่มี nonce doc กันนับซ้ำ ยิงซ้ำกี่รอบก็ไม่เกิน
// - ค่าจาก cloud ทำได้แค่ "ยกขึ้น" (max) ตัวนับในเครื่องไม่มีวันถอยหลัง
// - ข้อจำกัด: หลายเครื่องจ่ายพร้อมกันก่อนเห็นค่ากันและกัน ยังได้เลขซ้ำได้ (เหมือนของเดิม)

type CounterIncrement = {
  nonce: string; // hash ของบิล = id ของ nonce doc ใน Firestore
  month: string; // YYYYMM
  claimed: number; // เลขที่บิลนี้ได้ไป
  createdAt: number;
  failCount?: number;
};

const FIRESTORE_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_FLUSH_FAILURES = 20;
// error ที่เกิดจากเน็ต/เซิร์ฟเวอร์ชั่วคราว ส่งรอบหน้าก็ผ่านได้ ไม่นับเป็นความล้มเหลว
// (เช่น WiFi ต่ออยู่แต่ไม่มีอินเทอร์เน็ต navigator.onLine ยังเป็น true)
const TRANSIENT_FIRESTORE_CODES = new Set([
  "unavailable",
  "deadline-exceeded",
  "aborted",
  "resource-exhausted",
  "cancelled",
]);
const PRIME_TIMEOUT_MS = 5000;
const FLUSH_INTERVAL_MS = 10 * 60 * 1000;

export const useReceiptIdCounterStore = defineStore("receiptIdCounter", () => {
  const receiptsCounter = ref(0);
  const counterYYYYMM = ref("");
  // ร้านที่ receiptsCounter ในหน่วยความจำเป็นของ — เครื่องเดียวสลับได้หลายร้าน
  let counterStoreId = "";

  const storeId = (): string => useFirebaseFirestore().getStoreid() || "";
  const isOnline = () => typeof navigator === "undefined" || navigator.onLine !== false;

  const counterKey = (month: string) => `receiptsCounter:${storeId()}:${month}`;
  const queuePrefix = () => `counter-increment:${storeId()}:`;
  // รายการที่ fail เกิน cap ย้ายมาพักไว้ (ไม่ลบ เลขใบเสร็จสำคัญ) จะได้ไม่วนยิงซ้ำไปตลอด
  const parkedPrefix = () => `counter-increment-parked:${storeId()}:`;

  const readLocal = (month: string): number => {
    try {
      const n = parseInt(localStorage.getItem(counterKey(month)) ?? "", 10);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  // ไม่มีวันลด: max ของ memory, localStorage และค่าใหม่
  const raiseTo = (value: number, month: string) => {
    const next = Math.max(readLocal(month), value);
    try {
      localStorage.setItem(counterKey(month), String(next));
    } catch {}
    if (month === counterYYYYMM.value && storeId() === counterStoreId) {
      receiptsCounter.value = Math.max(receiptsCounter.value, next);
    }
  };

  // ── Cloud → local ────────────────────────────────────────────────
  let unsubCloud: Unsubscribe | null = null;

  const watchCloudCounter = () => {
    unsubCloud?.();
    unsubCloud = null;
    const month = counterYYYYMM.value;
    const sid = storeId();
    if (!month || !sid) return;
    unsubCloud = onSnapshot(
      useFirebaseFirestore().receiptCounterDocRef(month),
      (snap) => {
        // callback ค้างจากร้าน/เดือนก่อนหน้า ห้ามเขียนทับ key ปัจจุบัน
        if (sid !== storeId()) return;
        raiseTo(Number(snap.data()?.value) || 0, month);
      },
      (error) => console.error("receiptIdCounter: onSnapshot failed", error),
    );
  };

  // เปลี่ยนเดือน (เปิดเครื่องค้างข้ามเดือน) หรือสลับร้าน ต้องสลับ key และ listener ตาม
  // ไม่งั้นเลขในหน่วยความจำของร้าน/เดือนก่อนจะติดไปใช้ต่อ
  const switchMonth = (month: string) => {
    if (!month) return;
    if (month === counterYYYYMM.value && storeId() === counterStoreId) return;
    counterYYYYMM.value = month;
    counterStoreId = storeId();
    receiptsCounter.value = readLocal(month);
    if (watching) watchCloudCounter();
  };

  // ── Seed ครั้งแรกของเดือนบนเครื่องนี้ ─────────────────────────────
  // ยังไม่มีเลขในเครื่อง → อ่าน cloud ครั้งเดียวก่อนออกเลข จะได้ต่อจากเลขเดิม ไม่เริ่ม 0001 ใหม่
  const primeTasks = new Map<string, Promise<void>>();

  const primeFromCloudOnce = (month: string): Promise<void> => {
    const taskKey = `${storeId()}:${month}`;
    if (readLocal(month) > 0 || !isOnline() || !storeId()) return Promise.resolve();
    if (!primeTasks.has(taskKey)) {
      const task = (async () => {
        let value = 0;
        try {
          const snap = await getDoc(useFirebaseFirestore().receiptCounterDocRef(month));
          value = Number(snap.data()?.value) || 0;
        } catch (error) {
          console.error("receiptIdCounter: read Firestore counter failed", error);
        }
        raiseTo(value, month);
      })();
      primeTasks.set(taskKey, task);
    }
    return Promise.race([
      primeTasks.get(taskKey)!,
      new Promise<void>((resolve) => setTimeout(resolve, PRIME_TIMEOUT_MS)),
    ]);
  };

  const monthOf = (day: string) => (day || "").substring(0, 7).replace("-", "");

  // เรียกก่อนออกเลข (ใน payment.submit) ให้แน่ใจว่าเดือนถูกและ seed แล้ว
  const ready = async (businessDate: string) => {
    const month = monthOf(businessDate);
    switchMonth(month);
    await primeFromCloudOnce(month);
  };

  // ── Claim ────────────────────────────────────────────────────────
  const getNextReceiptIdAndIncrement = (nonce: string, month: string): number => {
    switchMonth(month);
    const next = Math.max(receiptsCounter.value, readLocal(month)) + 1;
    receiptsCounter.value = next;
    try {
      localStorage.setItem(counterKey(month), String(next));
      const cmd: CounterIncrement = { nonce, month, claimed: next, createdAt: Date.now() };
      localStorage.setItem(`${queuePrefix()}${nonce}`, JSON.stringify(cmd));
    } catch (error) {
      console.error("receiptIdCounter: persist claim failed", error);
    }
    void flushCounterIncrements();
    return next;
  };

  // ── Cloud sync queue ─────────────────────────────────────────────
  const readQueue = (): { cmd: CounterIncrement; key: string }[] => {
    const out: { cmd: CounterIncrement; key: string }[] = [];
    try {
      for (const key of Object.keys(localStorage)) {
        if (!key.startsWith(queuePrefix())) continue;
        const raw = localStorage.getItem(key);
        if (raw) out.push({ cmd: JSON.parse(raw), key });
      }
    } catch (error) {
      console.error("receiptIdCounter: read queue failed", error);
    }
    return out.sort((a, b) => a.cmd.createdAt - b.cmd.createdAt);
  };

  // nonce doc กับการยกค่า counter อยู่ใน transaction เดียว ยิงซ้ำเจอ nonce แล้วจะไม่ทำอะไร
  // ยกไปอย่างน้อยเท่า claimed ลำดับเลยไม่เพี้ยนแม้คิวแต่ละเครื่องมาถึงไม่ตามลำดับ
  const applyToCloud = async (cmd: CounterIncrement) => {
    const fs = useFirebaseFirestore();
    const counterRef = fs.receiptCounterDocRef(cmd.month);
    const nonceRef = fs.receiptCounterNonceDocRef(cmd.month, cmd.nonce);
    const expireAt = Timestamp.fromMillis(Date.now() + FIRESTORE_TTL_MS);
    await runTransaction(getFirestore(), async (tx) => {
      if ((await tx.get(nonceRef)).exists()) return;
      const base = Number((await tx.get(counterRef)).data()?.value) || 0;
      tx.set(counterRef, { value: Math.max(base + 1, Number(cmd.claimed) || 0) }, { merge: true });
      tx.set(nonceRef, { createdAt: cmd.createdAt, expireAt });
    });
  };

  // ตัวที่ fail ค้างคิวไว้รอรอบหน้า (บิลถัดไป / กลับมาออนไลน์ / ทุก 10 นาที)
  let flushing = false;
  const flushCounterIncrements = async () => {
    if (flushing || !isOnline() || !storeId()) return;
    flushing = true;
    try {
      for (const { cmd, key } of readQueue()) {
        try {
          await applyToCloud(cmd);
          localStorage.removeItem(key);
        } catch (error: any) {
          console.error("receiptIdCounter: flush failed", cmd.nonce, error);
          // เน็ตใช้ไม่ได้ ตัวที่เหลือก็ไม่ผ่านเหมือนกัน เก็บคิวไว้ทั้งหมดแล้วรอรอบหน้า
          if (TRANSIENT_FIRESTORE_CODES.has(error?.code)) break;
          try {
            const failCount = (cmd.failCount || 0) + 1;
            if (failCount >= MAX_FLUSH_FAILURES) {
              localStorage.setItem(`${parkedPrefix()}${cmd.nonce}`, JSON.stringify({ ...cmd, failCount }));
              localStorage.removeItem(key);
            } else {
              localStorage.setItem(key, JSON.stringify({ ...cmd, failCount }));
            }
          } catch {}
        }
      }
    } finally {
      flushing = false;
    }
  };

  // ── Boot ─────────────────────────────────────────────────────────
  let booted = false;
  // เริ่มฟัง cloud หลัง seed ตอน boot เสร็จ หลังจากนั้น switchMonth ย้าย listener ให้เอง
  let watching = false;
  const boot = async (businessDate: string) => {
    if (!process.client || booted) return;
    booted = true;
    const month = monthOf(businessDate);
    switchMonth(month);
    await primeFromCloudOnce(month);
    watching = true;
    watchCloudCounter();

    // แท็บอื่นของเครื่องนี้ออกเลขไป — `storage` event ทำงานแม้ออฟไลน์
    window.addEventListener("storage", (e) => {
      if (e.key !== counterKey(counterYYYYMM.value)) return;
      const n = parseInt(e.newValue ?? "", 10);
      if (Number.isFinite(n) && n > receiptsCounter.value) receiptsCounter.value = n;
    });
    window.addEventListener("online", () => void flushCounterIncrements());
    setInterval(() => void flushCounterIncrements(), FLUSH_INTERVAL_MS);
    void flushCounterIncrements();
  };

  return {
    receiptsCounter,
    counterYYYYMM,
    boot,
    ready,
    getNextReceiptIdAndIncrement,
    flushCounterIncrements,
  };
});
